/* ============================================================
   Silk Route — SR2 antique interactive globe (story page)
   Vanilla port of the CRO prototype globe (design handoff 2026-07).
   Requires: d3 v7 + topojson-client v3, loaded with `defer` by
   sections/sr2-globe.liquid, and a JSON config element:
     <script type="application/json" id="sr2-globe-data">
       { worldUrl, selected, routeOrder, markers:[{key,lon,lat}], labels:[{n,c:[lon,lat]}] }
     </script>
   Canvas: [data-sr2-globe-canvas]
   API:    window.SR2Globe.select(key)
   Emits:  CustomEvent 'sr2:globe:select' {detail:{key}} on document
           when a marker is clicked on the globe.
   Respects prefers-reduced-motion: auto-rotation starts paused.
   ============================================================ */
(function () {
  'use strict';

  var canvas = null, ctx = null, size = 0, proj = null, path = null;
  var rot = [-72, -16, 0];
  var reduceMotion = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  var auto = !reduceMotion;
  var drag = false, inited = false, raf = null, visible = false;
  var sphere = { type: 'Sphere' };
  var countries = null, borders = null, grat = null, route = null, labels = null;
  var markers = [];
  var markerByKey = {};
  var selectedKey = 'snow';

  function resumeAuto(delay) {
    if (reduceMotion) return;
    setTimeout(function () { auto = true; }, delay);
  }

  function tryInit(tries) {
    var d3 = window.d3, topojson = window.topojson;
    canvas = canvas || document.querySelector('[data-sr2-globe-canvas]');
    var el = document.getElementById('sr2-globe-data');
    if (!canvas || !el || !d3 || !d3.geoOrthographic || !topojson) {
      if ((tries || 0) < 80) setTimeout(function () { tryInit((tries || 0) + 1); }, 150);
      return;
    }
    if (inited) return;
    inited = true;

    var cfg;
    try { cfg = JSON.parse(el.textContent); } catch (e) { inited = false; return; }
    if (cfg.selected) selectedKey = cfg.selected;

    markers = (cfg.markers || []).map(function (m) {
      return { key: m.key, lon: m.lon, lat: m.lat };
    });
    markers.forEach(function (m) { markerByKey[m.key] = m; });
    labels = cfg.labels || [];

    fetch(cfg.worldUrl).then(function (r) { return r.json(); }).then(function (world) {
      countries = topojson.feature(world, world.objects.countries);
      borders = topojson.mesh(world, world.objects.countries, function (a, b) { return a !== b; });
      grat = d3.geoGraticule10();
      var byKey = {};
      markers.forEach(function (m) { byKey[m.key] = [m.lon, m.lat]; });
      route = {
        type: 'LineString',
        coordinates: (cfg.routeOrder || []).map(function (k) { return byKey[k]; }).filter(Boolean)
      };
      initCanvas();
      attach();
      startVisibilityLoop();
      window.addEventListener('resize', function () { if (canvas && proj) initCanvas(); });
    }).catch(function () { inited = false; });
  }

  function initCanvas() {
    var d3 = window.d3;
    if (!canvas) return;
    var rect = canvas.getBoundingClientRect();
    size = Math.max(280, Math.round(rect.width || 360));
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = size * dpr;
    canvas.height = size * dpr;
    ctx = canvas.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    proj = d3.geoOrthographic().scale(size / 2 - 8).translate([size / 2, size / 2]).clipAngle(90).rotate(rot);
    path = d3.geoPath(proj, ctx);
  }

  function loop() {
    // Stop the per-frame redraw whenever the globe is scrolled off-screen —
    // otherwise it pegs the CPU/GPU for the whole page. Restarted by the
    // IntersectionObserver in startVisibilityLoop() when it re-enters view.
    if (!visible) { raf = null; return; }
    if (auto && !drag && proj) { rot[0] += 0.045; proj.rotate(rot); }
    draw();
    raf = requestAnimationFrame(loop);
  }

  function startLoop() {
    if (raf == null && visible) { raf = requestAnimationFrame(loop); }
  }

  function startVisibilityLoop() {
    if (!('IntersectionObserver' in window) || !canvas) { visible = true; startLoop(); return; }
    var io = new IntersectionObserver(function (entries) {
      visible = entries[0].isIntersecting;
      if (visible) startLoop();
    }, { threshold: 0.01 });
    io.observe(canvas);
  }

  function draw() {
    var d3 = window.d3;
    if (!ctx || !proj) return;
    ctx.clearRect(0, 0, size, size);

    /* halo behind the sphere */
    var hal = ctx.createRadialGradient(size / 2, size / 2, size * 0.30, size / 2, size / 2, size * 0.62);
    hal.addColorStop(0, 'rgba(230,197,107,0.18)');
    hal.addColorStop(1, 'rgba(230,197,107,0)');
    ctx.fillStyle = hal;
    ctx.fillRect(0, 0, size, size);

    /* sepia ocean */
    ctx.beginPath(); path(sphere);
    var g = ctx.createRadialGradient(size * 0.36, size * 0.30, size * 0.06, size * 0.5, size * 0.52, size * 0.58);
    g.addColorStop(0, '#31220f');
    g.addColorStop(0.62, '#1a1108');
    g.addColorStop(1, '#0b0703');
    ctx.fillStyle = g;
    ctx.fill();

    /* gold rim glow */
    ctx.save();
    ctx.beginPath(); path(sphere);
    ctx.shadowColor = 'rgba(230,197,107,0.55)';
    ctx.shadowBlur = 24;
    ctx.lineWidth = 1.5;
    ctx.strokeStyle = 'rgba(230,197,107,0.62)';
    ctx.stroke();
    ctx.restore();

    /* graticule */
    ctx.beginPath(); path(grat);
    ctx.lineWidth = 0.5;
    ctx.strokeStyle = 'rgba(230,197,107,0.14)';
    ctx.stroke();

    /* ochre country fills + borders */
    ctx.beginPath(); path(countries);
    ctx.fillStyle = 'rgba(214,172,92,0.40)';
    ctx.fill();
    ctx.beginPath(); path(borders);
    ctx.lineWidth = 0.5;
    ctx.strokeStyle = 'rgba(92,64,26,0.8)';
    ctx.stroke();

    /* dashed crimson trade route */
    ctx.beginPath(); path(route);
    ctx.setLineDash([2, 5]);
    ctx.lineWidth = 1.3;
    ctx.strokeStyle = 'rgba(224,69,26,0.8)';
    ctx.stroke();
    ctx.setLineDash([]);

    /* specular highlight */
    var spec = ctx.createRadialGradient(size * 0.36, size * 0.30, 2, size * 0.40, size * 0.34, size * 0.52);
    spec.addColorStop(0, 'rgba(255,248,225,0.20)');
    spec.addColorStop(0.5, 'rgba(255,248,225,0.05)');
    spec.addColorStop(1, 'rgba(255,248,225,0)');
    ctx.save();
    ctx.beginPath(); path(sphere);
    ctx.clip();
    ctx.fillStyle = spec;
    ctx.fillRect(0, 0, size, size);
    ctx.restore();

    /* country name labels, front-facing only */
    var lc = [-rot[0], -rot[1]];
    if (labels) {
      ctx.save();
      ctx.font = '600 8.5px Hanken Grotesk, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      labels.forEach(function (L) {
        if (d3.geoDistance(L.c, lc) < Math.PI / 2) {
          var qp = proj(L.c);
          if (qp) {
            ctx.fillStyle = 'rgba(23,15,8,0.55)';
            ctx.fillText(L.n, qp[0] + 0.6, qp[1] + 0.6);
            ctx.fillStyle = 'rgba(255,246,220,0.82)';
            ctx.fillText(L.n, qp[0], qp[1]);
          }
        }
      });
      ctx.restore();
    }

    /* glowing markers, selected one pulses crimson */
    var center = [-rot[0], -rot[1]];
    var now = Date.now() / 600;
    markers.forEach(function (m) {
      var vis = d3.geoDistance([m.lon, m.lat], center) < Math.PI / 2;
      m._vis = vis;
      if (!vis) { m._sx = null; return; }
      var p = proj([m.lon, m.lat]);
      if (!p) { m._vis = false; return; }
      m._sx = p[0];
      m._sy = p[1];
      var on = m.key === selectedKey;
      var r = (on ? 7 : 4.2) * (on ? (1 + 0.16 * Math.sin(now)) : 1);
      ctx.beginPath();
      ctx.arc(p[0], p[1], r + (on ? 9 : 5), 0, 6.2832);
      ctx.fillStyle = on ? 'rgba(224,69,26,0.30)' : 'rgba(230,197,107,0.16)';
      ctx.fill();
      ctx.beginPath();
      ctx.arc(p[0], p[1], r, 0, 6.2832);
      ctx.fillStyle = on ? '#e0451a' : '#e6c56b';
      ctx.fill();
      ctx.lineWidth = 1.3;
      ctx.strokeStyle = 'rgba(255,248,230,0.95)';
      ctx.stroke();
    });
  }

  function attach() {
    if (!canvas || canvas._srAttached) return;
    canvas._srAttached = true;
    var px, py, moved;

    canvas.addEventListener('pointerdown', function (e) {
      drag = true;
      auto = false;
      px = e.clientX;
      py = e.clientY;
      moved = 0;
      canvas.style.cursor = 'grabbing';
      try { canvas.setPointerCapture(e.pointerId); } catch (err) { /* noop */ }
    });

    canvas.addEventListener('pointermove', function (e) {
      if (!drag) return;
      var dx = e.clientX - px, dy = e.clientY - py;
      px = e.clientX;
      py = e.clientY;
      moved += Math.abs(dx) + Math.abs(dy);
      rot[0] += dx * 0.32;
      rot[1] = Math.max(-88, Math.min(88, rot[1] - dy * 0.32));
      if (proj) proj.rotate(rot);
    });

    var up = function (e) {
      var wasDrag = moved > 6;
      drag = false;
      canvas.style.cursor = 'grab';
      resumeAuto(2600);
      if (!wasDrag) hit(e);
    };
    canvas.addEventListener('pointerup', up);
    canvas.addEventListener('pointercancel', function () {
      drag = false;
      resumeAuto(1500);
    });
  }

  function hit(e) {
    var rect = canvas.getBoundingClientRect();
    var x = e.clientX - rect.left, y = e.clientY - rect.top;
    var best = null, bd = 20;
    markers.forEach(function (m) {
      if (m._vis && m._sx != null) {
        var d = Math.hypot(m._sx - x, m._sy - y);
        if (d < bd) { bd = d; best = m; }
      }
    });
    if (best) {
      selectedKey = best.key;
      goTo(best.lon, best.lat);
      document.dispatchEvent(new CustomEvent('sr2:globe:select', { detail: { key: best.key } }));
    }
  }

  function goTo(lon, lat) {
    var d3 = window.d3;
    if (!proj || !d3) return;
    var iv = d3.interpolate(rot.slice(), [-lon, -lat, 0]);
    var t0 = performance.now(), dur = 850;
    auto = false;
    var step = function (now) {
      var k = Math.min(1, (now - t0) / dur);
      rot = iv(d3.easeCubicInOut(k));
      proj.rotate(rot);
      if (k < 1) {
        requestAnimationFrame(step);
      } else {
        resumeAuto(2600);
      }
    };
    requestAnimationFrame(step);
  }

  function select(key) {
    var m = markerByKey[key];
    if (!m) return;
    selectedKey = key;
    goTo(m.lon, m.lat);
  }

  window.SR2Globe = { select: select };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { tryInit(0); });
  } else {
    tryInit(0);
  }
})();
